import ReactGA from "@/utils/ga";
import { ANALYTICS_EVENTS } from "./events";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || "38dd89953b3aa1c780a27d2248a78c20";

const INTERNAL_ID_KEY = "rivyou_visitor_id";

// mixpanel-browser is ~100 KiB, so it is loaded as a separate chunk instead of in the main bundle.
// Events fired while it loads are queued and sent once it is ready.
let mixpanel = null;
let initStarted = false;
let initialized = false;
let sessionStartedAt = typeof window !== "undefined" ? Date.now() : null;
const MAX_PENDING_EVENTS = 100;
const pendingEvents = [];

/**
 * Returns a stable, per-browser internal ID. Generated once and persisted in
 * localStorage so the same visitor keeps the same ID across sessions; no login
 * required. This is the ID we assign to the Mixpanel profile via identify().
 */
function getOrCreateInternalId() {
  if (typeof window === "undefined") return null;

  try {
    let id = window.localStorage.getItem(INTERNAL_ID_KEY);
    if (!id) {
      const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      id = `web_${uuid}`;
      window.localStorage.setItem(INTERNAL_ID_KEY, id);
    }
    return id;
  } catch (error) {
    // localStorage can be unavailable (private mode / blocked storage).
    return null;
  }
}

function detectBrowser(ua) {
  if (/Edg\//.test(ua)) return "Microsoft Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  return "Unknown";
}

function detectBrowserVersion(ua) {
  const patterns = [
    /Edg\/(\d+(?:\.\d+)?)/,
    /OPR\/(\d+(?:\.\d+)?)/,
    /Chrome\/(\d+(?:\.\d+)?)/,
    /Firefox\/(\d+(?:\.\d+)?)/,
    /Version\/(\d+(?:\.\d+)?).*Safari/,
  ];
  for (const pattern of patterns) {
    const match = ua.match(pattern);
    if (match) return match[1];
  }
  return "";
}

function detectOS(ua) {
  if (/Windows/.test(ua)) return "Windows";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Mac OS X|Macintosh/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown";
}

function detectDeviceType(ua) {
  if (/iPad|Tablet/.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone|iPod/.test(ua)) return "Mobile";
  return "Desktop";
}

/**
 * Assigns the internal ID to the current profile and populates the People
 * profile with device/browser info. Mixpanel geolocates City/Region/Country
 * from the request IP automatically whenever a people update is sent.
 */
function identifyVisitor() {
  if (typeof window === "undefined") return;

  const internalId = getOrCreateInternalId();
  if (internalId) {
    mixpanel.identify(internalId);
  }

  const ua = navigator.userAgent || "";

  // $browser / $os are Mixpanel-recognised profile properties and show up in
  // the Users table columns. Custom props (internal_id, device_type, …) show
  // up as additional profile fields.
  mixpanel.people.set({
    $browser: detectBrowser(ua),
    $browser_version: detectBrowserVersion(ua),
    $os: detectOS(ua),
    $screen_width: window.screen ? window.screen.width : undefined,
    $screen_height: window.screen ? window.screen.height : undefined,
    $current_url: window.location.href,
    $referring_domain: document.referrer ? new URL(document.referrer).hostname : "",
    internal_id: internalId,
    device_type: detectDeviceType(ua),
    language: navigator.language || "",
    platform: "website",
    app: "rivyou-marketing",
    last_seen: new Date().toISOString(),
  });

  // Set only on first profile creation so we keep the true first-touch data.
  mixpanel.people.set_once({
    $created: new Date().toISOString(),
    first_landing_page: window.location.pathname,
    first_referrer: document.referrer || "direct",
  });
}

function getPageContext() {
  if (typeof window === "undefined") {
    return { path: "/", referrer: "", page_url: "" };
  }

  return {
    path: window.location.pathname,
    referrer: document.referrer || "",
    page_url: window.location.href,
  };
}

function getStoreFromHref(href) {
  if (!href) return "unknown";
  if (href.includes("apps.apple.com")) return "ios";
  if (href.includes("play.google.com")) return "android";
  return "unknown";
}

function flushPendingEvents() {
  while (pendingEvents.length) {
    const { eventName, properties } = pendingEvents.shift();
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(`Failed to track event ${eventName}:`, error);
    }
  }
}

export function initAnalytics() {
  if (initStarted || typeof window === "undefined") return;
  initStarted = true;

  import("mixpanel-browser")
    .then((module) => {
      mixpanel = module.default;

      mixpanel.init(MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV === "development",
        track_pageview: false,
        persistence: "localStorage",
        ignore_dnt: false,
      });

      mixpanel.register({
        platform: "website",
        app: "rivyou-marketing",
      });

      identifyVisitor();

      initialized = true;
      flushPendingEvents();
    })
    .catch((error) => {
      console.error("Failed to initialize Mixpanel:", error);
      pendingEvents.length = 0;
    });
}

export function isAnalyticsReady() {
  return initialized;
}

export function trackEvent(eventName, properties = {}) {
  // Page context is captured now, not at flush time, so queued events keep the path they happened on.
  const payload = { ...getPageContext(), ...properties };

  if (!initialized) {
    // initAnalytics() is deferred until the first interaction / a few seconds after load: queue until then.
    if (pendingEvents.length < MAX_PENDING_EVENTS) pendingEvents.push({ eventName, properties: payload });
    return;
  }

  try {
    mixpanel.track(eventName, payload);
  } catch (error) {
    console.error(`Failed to track event ${eventName}:`, error);
  }
}

export function trackPageView(pathname) {
  trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
    path: pathname || getPageContext().path,
  });
}

export function trackDownloadClick({ location, href }) {
  ReactGA.event({
    category: "CTA",
    action: "click",
    label: "Download App",
    location,
    store: getStoreFromHref(href),
  });

  trackEvent(ANALYTICS_EVENTS.DOWNLOAD_CLICKED, {
    location,
    store: getStoreFromHref(href),
    destination_url: href,
  });
}

export function trackScrollDepth(scrollDepth, pathname) {
  trackEvent(ANALYTICS_EVENTS.PAGE_SCROLLED, {
    scroll_depth: scrollDepth,
    path: pathname || getPageContext().path,
  });
}

export function trackSessionEnded() {
  if (!initialized || sessionStartedAt == null) return;

  const durationSeconds = Math.round((Date.now() - sessionStartedAt) / 1000);

  trackEvent(ANALYTICS_EVENTS.SESSION_ENDED, {
    duration_seconds: durationSeconds,
  });
}

export function createDownloadClickHandler(location, href) {
  return () => trackDownloadClick({ location, href });
}
